import { executeRecaptcha } from '@/composables/useRecaptcha';
import BaseService from './BaseService';

const resource = 'api/v3/';

// People Service responde 401 tanto por token de captcha ausente/inválido como
// por problemas de autenticación normales. Se distingue por el detalle del body
// para no confundirlo con una sesión expirada.
const CAPTCHA_ERROR_PATTERN = /captcha token/i;

const isInvalidCaptchaError = (error) => {
  if (error?.response?.status !== 401) return false;
  const { data } = error.response;
  const detail = typeof data === 'string' ? data : (data?.detail ?? data?.message ?? '');
  return CAPTCHA_ERROR_PATTERN.test(detail);
};

class AuthenticationService extends BaseService {
  constructor() {
    super({ resource });
  }

  async login(form) {
    const action = 'token/';
    return this.http.post(this.resource + action, form);
  }

  async register(form) {
    const action = 'users/register/';
    return this.http.post(this.resource + action, form);
  }

  async userInfo() {
    const action = 'token/user-info/';
    return this.http.get(this.resource + action);
  }

  /**
   * Consulta los datos de un ciudadano por cédula en People Service.
   *
   * El endpoint exige el header `X-Recaptcha-Token` con un token de reCAPTCHA
   * v2 invisible. El token es de un solo uso y expira a los ~2 minutos, así que
   * se ejecuta el challenge en cada intento y nunca se reutiliza.
   *
   * Ante un 401 por token inválido (expirado, ya usado o de otra Site Key) se
   * reintenta una única vez con un token nuevo. El 503 del servicio de
   * verificación de Google lo reintenta con backoff el interceptor de
   * BaseService.
   */
  async getDataFromPeopleService(idNumber) {
    const action = `citizens/${idNumber}/details/`;
    const url = this.resource + action;

    // El token es de un solo uso: si BaseService reintenta por un 503, debe
    // ejecutar el challenge de nuevo en vez de reenviar el token ya consumido.
    const buildCaptchaHeaders = async () => ({
      'X-Recaptcha-Token': await executeRecaptcha(),
    });

    const requestWithCaptcha = async () => this.http.get(url, {
      headers: await buildCaptchaHeaders(),
      __refreshHeaders: buildCaptchaHeaders,
    });

    try {
      return await requestWithCaptcha();
    } catch (error) {
      if (isInvalidCaptchaError(error)) {
        console.warn('[AuthenticationService] Token de captcha inválido, reintentando una vez.');
        return requestWithCaptcha();
      }
      throw error;
    }
  }

  async resetPassword(form) {
    const action = 'token/rest-auth/password/reset/';
    return this.http.post(this.resource + action, form);
  }

  async recoverPassword(form) {
    const action = 'token/rest-auth/password/reset/confirm/';
    return this.http.post(this.resource + action, form);
  }
}

export default AuthenticationService;
